import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from '@tanstack/react-router';
import { PlatformRole, platformRoleAtLeast, type AdminOrgDetail, type OrgRole } from '@ddp/shared';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import ArrowLeft from '@carbon/icons-react/es/ArrowLeft';

import { apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { PageHeader } from '../components/PageHeader.js';
import { formatBytes, formatCompact, quotaRatio } from '../features/admin/format.js';
import { ArchitectureSchema } from '../features/admin/ArchitectureSchema.js';
import { useArchitectureHealth } from '../features/admin/use-architecture-health.js';

/** Libellés de rôle côté utilisateur (identifiants en anglais, affichage en français). */
const ROLE_LABEL: Record<OrgRole, string> = {
  VIEWER: 'Lecteur',
  ANNOTATOR: 'Annotateur',
  EDITOR: 'Éditeur',
  ADMIN: 'Administrateur',
  OWNER: 'Propriétaire',
};

/**
 * Détail d'une organisation (ADR-0025, incrément 3) : stockage + membres avec leur consommation par
 * utilisateur sur la fenêtre. Réservé aux exploitants (SUPPORT+) ; le back garde la surface (404).
 */
export function AdminOrgDetailPage() {
  const { organizationId } = useParams({ strict: false }) as { organizationId: string };
  const currentUser = useAuthStore((state) => state.currentUser);
  const canView = currentUser
    ? platformRoleAtLeast(currentUser.platformRole, PlatformRole.SUPPORT)
    : false;

  const [detail, setDetail] = useState<AdminOrgDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiFetch<AdminOrgDetail>(`/admin/organizations/${organizationId}`);
        if (active) setDetail(data);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [canView, organizationId]);

  if (!canView) {
    return (
      <section className="ddp-page__section">
        <PageHeader title="Organisation" />
        <InlineNotification
          kind="info"
          title="Accès réservé"
          subtitle="Réservé aux exploitants de la plateforme."
          lowContrast
          hideCloseButton
        />
      </section>
    );
  }

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description="Chargement…" />
      </section>
    );
  }

  const ratio = detail ? quotaRatio(detail.storageUsedBytes, detail.storageQuotaBytes) : null;

  return (
    <section className="ddp-page__section ddp-admin">
      <RouterLink to="/admin" className="ddp-admin__back">
        <ArrowLeft size={16} /> Toutes les organisations
      </RouterLink>

      {error && (
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle={error}
          lowContrast
          onClose={() => setError(null)}
        />
      )}

      {detail && (
        <>
          <PageHeader
            title={detail.name}
            lead={`${detail.slug} — consommation sur ${detail.windowDays} jours`}
          />

          <div className="ddp-admin__totals">
            <div className="ddp-admin__total">
              <span className="ddp-admin__total-value">{formatBytes(detail.storageUsedBytes)}</span>
              <span className="ddp-admin__total-label">
                stockage / {formatBytes(detail.storageQuotaBytes)}
              </span>
            </div>
            {ratio !== null && (
              <div className="ddp-admin__total">
                <span className="ddp-admin__total-value">{Math.round(ratio * 100)} %</span>
                <span className="ddp-admin__total-label">du quota</span>
              </div>
            )}
          </div>

          <h2 className="ddp-admin__subtitle">
            Membres <span className="ddp-admin__count">{detail.members.length}</span>
          </h2>
          <div className="ddp-admin__table-wrap">
            <table className="ddp-admin__table">
              <thead>
                <tr>
                  <th scope="col">Membre</th>
                  <th scope="col">Rôle</th>
                  <th scope="col" className="ddp-admin__num">
                    Jetons IA
                  </th>
                  <th scope="col" className="ddp-admin__num">
                    Transfert
                  </th>
                  <th scope="col" className="ddp-admin__num">
                    Stockage
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail.members.map((member) => (
                  <tr key={member.userId}>
                    <td>
                      <span className="ddp-admin__org-name">{member.displayName}</span>
                      <span className="ddp-admin__org-slug">{member.email}</span>
                    </td>
                    <td>{ROLE_LABEL[member.role]}</td>
                    <td className="ddp-admin__num">{formatCompact(member.aiTokens)}</td>
                    <td className="ddp-admin__num">{formatBytes(member.dataTransferBytes)}</td>
                    <td className="ddp-admin__num">{formatBytes(member.storageBytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="ddp-admin__note">
            Transfert : borne haute (octets autorisés à l’ouverture d’une vidéo). Stockage : octets
            des vidéos téléversées par le membre.
          </p>

          <OrgArchitectureHealth organizationId={organizationId} enabled={canView} />
        </>
      )}
    </section>
  );
}

/**
 * État d'architecture d'une organisation vu par l'éditeur (ADR-0037 §4). Servi uniquement si
 * l'organisation a **consenti** au partage : sinon l'API renvoie 404 (on ne confirme pas une surface
 * non autorisée), rendu ici comme une mention neutre « non partagé ».
 */
function OrgArchitectureHealth({
  organizationId,
  enabled,
}: {
  organizationId: string;
  enabled: boolean;
}) {
  const { health, loading, errorStatus } = useArchitectureHealth(
    `/admin/organizations/${organizationId}/architecture-health`,
    enabled,
  );

  return (
    <section className="ddp-admin__arch">
      <h2 className="ddp-admin__subtitle">État de l’architecture</h2>
      {loading && !health ? (
        <Loading withOverlay={false} description="Chargement…" />
      ) : errorStatus === 404 ? (
        <InlineNotification
          kind="info"
          title="Non partagé"
          subtitle="Cette organisation n’a pas autorisé le partage de son état d’architecture."
          lowContrast
          hideCloseButton
        />
      ) : (
        health && <ArchitectureSchema health={health} />
      )}
    </section>
  );
}
